import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { api, fieldError } from "@/lib/api";
import { fromLocalInput, toLocalInput } from "@/lib/dates";
import { AreaPicker, Badge, Button, ConfirmButton, ErrorText, Field, ImageUpload, Input, Section, Textarea, Toggle } from "@/ui/kit";
import { PageHeader } from "@/ui/Shell";

interface Ev {
  id: string;
  title: string;
  description: string;
  startsAt: string;
  endsAt: string;
  venueName: string;
  address: string;
  lat: number | null;
  lng: number | null;
  capacity: number | null;
  hostName: string | null;
  imageKey: string | null;
  imageUrl: string | null;
  areaIds: number[];
  allAreas: boolean;
  includeAdjacent: boolean;
  status: "draft" | "published" | "cancelled";
  going: number;
  maybe: number;
  waitlist: number;
}

function defaultStart() {
  const d = new Date(Date.now() + 7 * 86_400_000);
  d.setHours(10, 0, 0, 0);
  return d;
}

export default function EventEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-event", id], queryFn: () => api<Ev>(`/v1/admin/events/${id}`), enabled: !isNew });

  const [f, setF] = useState({
    title: "", description: "", startsAt: toLocalInput(defaultStart()), endsAt: toLocalInput(new Date(defaultStart().getTime() + 90 * 60_000)),
    venueName: "", address: "", capacity: "", hostName: "Modern Motherhood team", imageId: null as string | null, imageUrl: null as string | null,
    areaIds: [] as number[], allAreas: false, includeAdjacent: true, lat: null as number | null, lng: null as number | null,
  });
  const [found, setFound] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const e = q.data;

  useEffect(() => {
    if (!e) return;
    setF({
      title: e.title, description: e.description, startsAt: toLocalInput(e.startsAt), endsAt: toLocalInput(e.endsAt), venueName: e.venueName, address: e.address,
      capacity: e.capacity ? String(e.capacity) : "", hostName: e.hostName ?? "", imageId: e.imageKey, imageUrl: e.imageUrl, areaIds: e.areaIds, allAreas: e.allAreas,
      includeAdjacent: e.includeAdjacent, lat: e.lat, lng: e.lng,
    });
  }, [e]);

  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((s) => ({ ...s, [k]: v }));
  const body = () => ({
    title: f.title, description: f.description, startsAt: fromLocalInput(f.startsAt), endsAt: fromLocalInput(f.endsAt), venueName: f.venueName,
    address: f.address, lat: f.lat, lng: f.lng, capacity: f.capacity ? Number(f.capacity) : null, hostName: f.hostName || null,
    imageAttachmentId: f.imageId, allAreas: f.allAreas, areaIds: f.allAreas ? [] : f.areaIds, includeAdjacent: f.includeAdjacent,
  });

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ["admin-events"] });
      await qc.invalidateQueries({ queryKey: ["admin-event", id] });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  }

  async function save(publish: boolean) {
    await run(publish ? "publish" : "save", async () => {
      const r = isNew ? await api<{ id: string }>("/v1/admin/events", { method: "POST", body: body() }) : await api<{ id: string }>(`/v1/admin/events/${id}`, { method: "PUT", body: body() });
      if (publish) await api(`/v1/admin/events/${r.id}/publish`, { method: "POST" });
      if (isNew) nav(`/events/${r.id}`, { replace: true });
    });
  }

  return (
    <>
      <PageHeader title={isNew ? "New event" : (e?.title ?? "Event")} back="/events" action={e ? <Badge tone={e.status === "published" ? "sage" : e.status === "draft" ? "butter" : "blush"}>{e.status}</Badge> : null} />
      {e && e.status !== "draft" ? (
        <div className="mb-4 grid grid-cols-3 gap-2 text-center">
          {[["Going", e.going], ["Maybe", e.maybe], ["Waitlist", e.waitlist]].map(([l, n]) => (
            <div key={l} className="rounded-2xl bg-mist p-3"><p className="text-2xl font-bold">{n}</p><p className="text-xs text-muted">{l}</p></div>
          ))}
        </div>
      ) : null}

      <ImageUpload url={f.imageUrl} onUploaded={(aid, url) => setF((s) => ({ ...s, imageId: aid, imageUrl: url }))} onClear={() => setF((s) => ({ ...s, imageId: null, imageUrl: null }))} />
      <div className="h-4" />
      <Field label="Title" error={fieldError(error, "title")}>{(i) => <Input id={i} value={f.title} onChange={(ev) => set("title", ev.target.value)} placeholder="Stroller Walk + Coffee" />}</Field>
      <Field label="Description">{(i) => <Textarea id={i} value={f.description} onChange={(ev) => set("description", ev.target.value)} placeholder="What to expect, what to bring, who it's for…" />}</Field>
      <div className="grid gap-x-3 sm:grid-cols-2">
        <Field label="Starts">{(i) => <Input id={i} type="datetime-local" value={f.startsAt} onChange={(ev) => set("startsAt", ev.target.value)} />}</Field>
        <Field label="Ends" error={fieldError(error, "endsAt")}>{(i) => <Input id={i} type="datetime-local" value={f.endsAt} onChange={(ev) => set("endsAt", ev.target.value)} />}</Field>
      </div>
      <Field label="Venue name">{(i) => <Input id={i} value={f.venueName} onChange={(ev) => set("venueName", ev.target.value)} placeholder="Lincoln Park Conservatory" />}</Field>
      <Field label="Address" hint={found ? `📍 ${found}` : "We'll find it on the map when you save."}>
        {(i) => (
          <div className="flex gap-2">
            <Input id={i} value={f.address} onChange={(ev) => { set("address", ev.target.value); setF((s) => ({ ...s, lat: null, lng: null })); setFound(null); }} placeholder="2391 N Stockton Dr" />
            <Button
              type="button"
              variant="secondary"
              loading={busy === "geo"}
              onClick={() => run("geo", async () => {
                const g = await api<{ lat: number; lng: number; formatted: string }>("/v1/admin/geocode", { method: "POST", body: { address: f.address } });
                setF((s) => ({ ...s, lat: g.lat, lng: g.lng }));
                setFound(g.formatted);
              })}
            >
              Check
            </Button>
          </div>
        )}
      </Field>
      <div className="grid gap-x-3 sm:grid-cols-2">
        <Field label="Capacity" hint="Leave blank for unlimited. Extra RSVPs join a waitlist.">{(i) => <Input id={i} inputMode="numeric" value={f.capacity} onChange={(ev) => set("capacity", ev.target.value.replace(/\D/g, ""))} placeholder="Unlimited" />}</Field>
        <Field label="Hosted by">{(i) => <Input id={i} value={f.hostName} onChange={(ev) => set("hostName", ev.target.value)} />}</Field>
      </div>

      <Section title="Who sees it">
        <Toggle label="All of Chicago" hint="Every member sees it and gets the new-meetup notification." checked={f.allAreas} onChange={(v) => set("allAreas", v)} />
        {!f.allAreas ? (
          <div className="mt-3">
            <AreaPicker value={f.areaIds} onChange={(v) => set("areaIds", v)} />
            {fieldError(error, "areaIds") ? <p className="mt-1 text-sm text-danger">{fieldError(error, "areaIds")}</p> : null}
            <div className="mt-3">
              <Toggle label="Also show to neighboring areas" hint="On: moms in areas bordering these see it too. Off: only the areas above." checked={f.includeAdjacent} onChange={(v) => set("includeAdjacent", v)} />
            </div>
          </div>
        ) : null}
      </Section>

      <ErrorText error={error} />
      <div className="pb-safe sticky bottom-20 z-10 -mx-4 grid gap-2 bg-cream/95 px-4 py-3 backdrop-blur lg:bottom-0">
        {e?.status === "published" ? (
          <Button loading={busy === "save"} onClick={() => void save(false)}>Save changes</Button>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Button variant="ghost" loading={busy === "save"} onClick={() => void save(false)}>Save draft</Button>
            <Button variant="rose" loading={busy === "publish"} onClick={() => void save(true)}>Publish</Button>
          </div>
        )}
      </div>
      {e?.status === "published" ? (
        <Section title="Danger zone">
          <p className="mb-2 text-sm text-muted">Everyone who RSVP'd gets a notification.</p>
          <ConfirmButton label="Cancel this event" confirmLabel="Yes, cancel it" onConfirm={() => run("cancel", () => api(`/v1/admin/events/${id}/cancel`, { method: "POST", body: {} }))} className="w-full" />
        </Section>
      ) : null}
    </>
  );
}
